import { useState, useEffect, useCallback, useMemo } from 'react'
import debounce from 'lodash.debounce'
import {
  DataGrid,
  GridRowsProp,
  GridColDef,
  GridActionsCellItem,
  GridSlotsComponent,
  gridClasses,
  GridRowSelectionModel,
  GridPaginationModel,
  GridSortModel,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowHeightParams,
} from '@mui/x-data-grid'
import {
  Box,
  Fade,
  Paper,
  ListItemText,
  MenuList,
  MenuItem,
} from '@mui/material'
import Popper from '@mui/material/Popper'
import IconButton from '@mui/material/IconButton'
import FilterListIcon from '@mui/icons-material/FilterList'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Icon, Card, TypoGraph, Container, Popover } from 'components'
import { MultiSelectField } from 'components/controllers'
import clientSearch from 'tools/searchQuery'
import { GridColumnGroupingModel } from '@mui/x-data-grid/models/gridColumnGrouping'
import styles from './table.module.scss'

const dataGridSx = {
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
  },
  '.MuiDataGrid-columnSeparator--sideRight': {
    display: 'none',
  },
  '.MuiDataGrid-columnHeaderDraggableContainer': {
    width: 'auto',
  },
  '.MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .actionButton': {
    display: 'none',
  },
  '.MuiDataGrid-actionsCell': {
    position: 'absolute',
    right: '2rem',
  },
  [`& .${gridClasses.row}:hover`]: {
    '.actionButton': {
      display: 'block',
    },
  },
  '.MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '.MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '.MuiDataGrid-selectedRowCount ': {
    visibility: 'hidden',
  },
  background: 'transparent'
}

const dynamicRowHeightSx = {
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
  },
  '.MuiDataGrid-columnSeparator--sideRight': {
    display: 'none',
  },
  '.MuiDataGrid-columnHeaderDraggableContainer': {
    width: 'auto',
  },
  '.MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .actionButton': {
    display: 'none',
  },
  '.MuiDataGrid-actionsCell': {
    position: 'absolute',
    right: '2rem',
  },
  [`& .${gridClasses.row}:hover`]: {
    '.actionButton': {
      display: 'block',
    },
  },
  '.MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '.MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '.MuiDataGrid-selectedRowCount ': {
    visibility: 'hidden',
  },
  background: 'transparent',
  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { 
    py: '8px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '52px'
  },
  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { 
    py: '15px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '52px'
  },
  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { 
    py: '22px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '52px'
  },
  '.MuiDataGrid-cell': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    minHeight: '52px',
  },
  '.MuiDataGrid-cell--textLeft': {
    alignItems: 'flex-start',
  },
  '.MuiDataGrid-cell--textRight': {
    alignItems: 'flex-end',
  },
  '.MuiDataGrid-cell--textCenter': {
    alignItems: 'center',
  }
}


interface IActionList {
  id?: string
  icon: string
  name: string
  className?: string
  iconSize?: number
  selection?: boolean
  isDisabled?: (row: any) => any
  getLabel?: (row: any) => string
}

interface IFilterList {
  label: string
  key: string
}

interface IMenuListItems {
  label: string
  key: string
  action: () => void
}

interface ITableProps {
  id?: string
  loading?: boolean
  columns: GridColDef[]
  columnGroupingModel?: GridColumnGroupingModel
  columnVisibilityModel?: GridColumnVisibilityModel
  data: GridRowsProp
  totalCount?: number
  title?: string
  defaultPageSize?: number
  checkboxSelection?: boolean
  reduceHeight?: number
  autoHeight?: boolean
  paginationMode?: 'client' | 'server'
  columnHeaderHeight?: number
  actionList?: IActionList[]
  components?: Partial<GridSlotsComponent> | undefined
  filters?: IFilterList[]
  rowHeight?: number
  rowClassName?: (row: any) => any
  rowClick?: (row: any) => any
  showFilter?: boolean
  showTitle?: boolean
  showCaption?: boolean
  caption?: string
  multiSearchFilter?: boolean
  rowsPerPageOptions?: number[],
  disableSelectionOnClick?: boolean,
  customSelectionModel?: GridRowSelectionModel
  onSingleQuickFilterChange?: (value: any) => any
  getRowId?: (row: any) => any
  handleCheckboxSelection?: (selections: GridRowSelectionModel) => void
  handleActions?: (id: string | number, name: string) => void
  handleSelectionActions?: (
    name: string,
    selectionModel: GridRowSelectionModel
  ) => void
  handlePagination?: (pageCount: number, pageSize: number) => void
  onSelectionModelChange?: (newSelectionModel: GridRowSelectionModel) => void
  onSortChange?: (field: string, direction: string) => void
  sortField?: any
  sortOrder?: any
  menuList?: IMenuListItems[]
  apiRef?: any
  isRowSelectable?: (params: GridRowParams) => boolean
  editMode?: 'row' | 'cell'
  processRowUpdate?: any
  onProcessRowUpdateError?: any
  getRowHeight?: (params: GridRowHeightParams) => number | null
  useDynamicRowHeight?: boolean
  getEstimatedRowHeight?: (params: GridRowHeightParams) => number | null
  pagination?: boolean
  currentPage?: number
  pageSize?: number
  sx?: any
  tableClassName?: string
}

const TableComponent: React.FC<ITableProps> = (props) => {
  const {
    id,
    loading, // shows the loader inside the table
    data, // array of datas which will be paried from the column name
    totalCount, // table total count
    paginationMode = 'client', // you can change the paginationMode to handle pagination in server side or client side
    columns, // colums with array of objects
    columnGroupingModel,  // groups of columns
    columnVisibilityModel, // visibility for columns
    columnHeaderHeight = 50, // height for columns' headers
    defaultPageSize = 100,
    checkboxSelection = true, // To show or hide the checkbox selection in table
    actionList, // list of actions items to show while hover the row
    reduceHeight = 220, // reduces table height
    components,
    filters,
    rowHeight = 52,
    rowClassName,
    rowClick,
    title,
    showFilter = true,
    showTitle = true,
    showCaption = false,
    caption="",
    multiSearchFilter = true,
    rowsPerPageOptions = [5, 10, 20, 50, 100],
    autoHeight,
    disableSelectionOnClick,
    customSelectionModel=[],
    onSelectionModelChange,
    getRowId = (row: any) => row?.id, // to get unique row id,
    handleActions, // func to handle the actions
    handleCheckboxSelection, // func to handle the checkbox selection
    handlePagination, // func to handle the pagination
    onSingleQuickFilterChange, // func to handle quick search
    onSortChange, // func to handle sort changes
    sortField,
    sortOrder,
    menuList, //list of items for dot menu next to search bar
    apiRef,
    isRowSelectable,
    editMode,
    processRowUpdate,
    onProcessRowUpdateError,
    getRowHeight,
    useDynamicRowHeight,
    getEstimatedRowHeight,
    pagination = true,
    currentPage: currentPageProp,
    pageSize: pageSizeProp,
    sx,
    tableClassName = ''
  } = props
  const [pageSize, setPageSize] = useState(pageSizeProp ?? defaultPageSize)
  const [customColumns, setCustomColumns] = useState(() => columns)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  const [currentPage, setCurrentPage] = useState<number>(currentPageProp ?? 0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [filterPopper, setFilterPopper] = useState(null)
  const [tablePopover, setTablePopover] = useState(null)
  const [inputVal, setInputVal] = useState('')
  const [key, setMultiKey] = useState<number>(0)
  const [tableData, setTableData] = useState<any>(() => data ?? [])
  const [filterValues, setFilterValues] = useState<any>({
    freeSearches: [],
  })
  
  /*
   * function to build list of action cells
   * {param} id => will contain the row ID and will be passed to handleActions
   * ${return} => list of action array
   */

  const handleSelectionModelChange = (newSelection: GridRowSelectionModel) => {
    setSelectionModel(newSelection);
    onSelectionModelChange && onSelectionModelChange(newSelection);
    handleCheckboxSelection && handleCheckboxSelection(newSelection);
    return newSelection;
  };

  const buildActionData = useCallback(
    (id: number | string, row: any) => {
      const actionItems = actionList?.map((item) => {
        return (
          <GridActionsCellItem
            id={item.id}
            icon={<Icon name={item?.icon} size={item?.iconSize || 25} />}
            label={item.getLabel ? item.getLabel(row) : item?.name}
            showInMenu={true}
            onClick={() => handleActions && handleActions(id, item?.name)}
            disabled={item?.isDisabled ? item.isDisabled(row) : false}
          />
        )
      })
      return actionItems
    },
    [actionList, handleActions]
  )

  /**
   * This effect is used to merge the action object with columns
   * This logic will only if we list of actions from the props
   */
  useEffect(() => {
    const action = {
      field: 'actions',
      type: 'actions' as 'actions',
      width: 0,
      cellClassName: 'actions',
      getActions: ({ id, row }: { id: string | number, row: any }) => buildActionData(id, row),
    }
    const actionsLength = actionList?.length
    if (actionsLength) {
      setCustomColumns([...columns, action])
    } else {
      setCustomColumns(columns)
    }
  }, [actionList, columns, buildActionData])

  useEffect(() => {
    if (data) {
      setTableData(data)
      setSelectionModel(customSelectionModel);
    }
  }, [data])

  // Sync internal state with props when they change (for external control)
  useEffect(() => {
    if (currentPageProp !== undefined) {
      setCurrentPage(currentPageProp)
    }
  }, [currentPageProp])

  useEffect(() => {
    if (pageSizeProp !== undefined) {
      setPageSize(pageSizeProp)
    }
  }, [pageSizeProp])

  const handleSingleQuickFilter = useCallback(
    (val: any) => {
      // If using server-side pagination, skip local filtering
      if (paginationMode === 'server') {
        return
      }
      
      if (!val) {
        setTableData(data)
      } else {
        const searchedData = clientSearch(val, data)
        setTableData(searchedData)
      }
    },
    [data, paginationMode]
  )

  const debouncedChangeHandler = useMemo(
    () => debounce((val) => handleSingleQuickFilter(val), 1000),
    [handleSingleQuickFilter]
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel()
    }
  }, [debouncedChangeHandler])

  const handleRecordVal = (val: string) => {
    setFilterValues((prev: any) => ({
      ...prev,
      freeSearches: [...prev.freeSearches, val],
    }))
    setInputVal('')
    setAnchorEl(null)
    setMultiKey(Math.random() * 1000)
  }

  const handleFilterKey = (val: any) => {
    setFilterPopper(null)
  }

  const handleTableCustomize = (val: any) => {
    setTablePopover(null)
  }

  return (
    <Card sxContent={{ padding: 0, paddingBottom: '0!important' }}>
      {showTitle && (
        <Container sx={{ padding: '1rem' }}>
          <TypoGraph variant="h2" content={title ? title : 'Search & Filter'} />
        </Container>
      )}
      {showCaption && caption && (
        <Container sx={{ padding: '0 1rem 1rem 1rem' }}>
          <TypoGraph
            variant="caption"
            color="text.secondary"
            content={caption}
          />
        </Container>
      )}
      {showFilter && (
        <Container
          className={`${styles.filterContainer} ${
            menuList && menuList.length > 0
              ? styles.threeColumns
              : styles.twoColumns
          } ${loading ? styles.disabledContainer : ''}`}
        >
          {menuList && menuList.length > 0 && (
            <Container className={styles.moreContainer}>
              <IconButton
                id="actionsButtonTopRow"
                onClick={(e: any) => {
                  setTablePopover(e.target)
                  setAnchorEl(null)
                  setFilterPopper(null)
                }}
              >
                <MoreVertIcon htmlColor="grey" />
              </IconButton>
            </Container>
          )}
          <Container className={styles.filterIconContainer}>
            <FilterListIcon htmlColor="grey" />
          </Container>
          <Container>
            <MultiSelectField
              disabled={loading}
              options={[]}
              value={filterValues.freeSearches}
              freeSolo={true}
              placeholder="Search and Filter"
              className={styles.noFieldset}
              multiple={multiSearchFilter}
              onInputChange={(e: any, val: any) => {
                if(loading) return;
                setInputVal(val)
                setAnchorEl(e.target)
                setFilterPopper(null)
                !multiSearchFilter && debouncedChangeHandler(val)
                !multiSearchFilter &&
                  onSingleQuickFilterChange &&
                  onSingleQuickFilterChange(val)
              }}
              onChange={(e: any, val) => {
                setFilterValues((prev: any) => ({ ...prev, freeSearches: val }))
                setAnchorEl(null)
                setFilterPopper(e.target)
              }}
              onOpen={(e: any) => {
                setFilterPopper(e.target)
                setAnchorEl(null)
              }}
              onClose={(e) =>
                setTimeout(() => {
                  setFilterPopper(null)
                }, 300)
              }
            />
          </Container>
        </Container>
      )}
      <Box
        sx={{
          height: autoHeight ? 'auto' : `calc(100vh - ${reduceHeight}px)`,
          width: '100%',
        }}
      >
        <DataGrid
          apiRef={apiRef}
          sx={{
            ...(useDynamicRowHeight ? dynamicRowHeightSx : dataGridSx),
            ...sx
          }}
          rows={tableData}
          columns={customColumns}
          columnGroupingModel={columnGroupingModel}
          columnHeaderHeight={columnHeaderHeight}
          columnVisibilityModel={columnVisibilityModel}
          getRowId={getRowId}
          {...(pagination && { pagination: true })}
          {...(!pagination && { hideFooter: true })}
          {...(pagination && {
            paginationModel: { page: currentPage, pageSize: pageSize },
            onPaginationModelChange: (model, details) => {
              if (model.page !== undefined) {
                setCurrentPage(model.page)
              }
              if (model.pageSize !== undefined) {
                setPageSize(model.pageSize)
              }
              handlePagination &&
                handlePagination(
                  model.page ?? currentPage,
                  model.pageSize ?? pageSize
                )
            },
            pageSizeOptions: rowsPerPageOptions ? rowsPerPageOptions : [5, 10, 20, 50, 100]
          })}
          onResize={() => handleSelectionModelChange(selectionModel)}
          loading={loading}
          paginationMode={paginationMode}
          rowCount={totalCount}
          slots={components}
          checkboxSelection={checkboxSelection}
          onRowSelectionModelChange={(
            newSelectionModel: GridRowSelectionModel
          ) => {
            const filterList = handleSelectionModelChange(newSelectionModel)
            onSelectionModelChange && onSelectionModelChange(filterList)
            handleCheckboxSelection &&
              handleCheckboxSelection(filterList)
          }}
          rowSelectionModel={selectionModel}
          isRowSelectable={isRowSelectable}
          rowHeight={rowHeight}
          getRowClassName={rowClassName}
          onRowClick={rowClick}
          autoHeight={autoHeight}
          editMode={editMode}
          getRowHeight={useDynamicRowHeight ? () => 'auto' : getRowHeight}
          getEstimatedRowHeight={useDynamicRowHeight ? getEstimatedRowHeight : undefined}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          disableRowSelectionOnClick={disableSelectionOnClick}
          disableColumnFilter
          disableColumnMenu
          onSortModelChange={(model) => {
            if (onSortChange && model.length > 0) {
              const sortItem = model[0]
              onSortChange(sortItem.field, sortItem.sort || 'asc')
            } else if (onSortChange && model.length === 0) {
              onSortChange('', '')
            }
          }}
          initialState={{
            ...tableData.initialState,
            pagination: { paginationModel: { pageSize: pageSize } },
            sorting: {
              sortModel:
                sortField && sortOrder
                  ? [{ field: sortField, sort: sortOrder }]
                  : [],
            },
          }}
          className={tableClassName}
        />
      </Box>
      {multiSearchFilter && (
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <MenuList>
                  <MenuItem onClick={() => handleRecordVal(inputVal)}>
                    <ListItemText>{`Records contains ${inputVal}`}</ListItemText>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
      <Popper
        open={Boolean(filterPopper)}
        anchorEl={filterPopper}
        placement="bottom-start"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper>
              <MenuList>
                {filters?.map((item, index) => (
                  <MenuItem
                    onClick={() => handleFilterKey(item)}
                    key={`key-${index}`}
                  >
                    <ListItemText>{item.label}</ListItemText>
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Fade>
        )}
      </Popper>
      {/* Table customization popover ex: column hide/show, density, download options  */}
      <Popover
        open={Boolean(tablePopover)}
        anchorEl={tablePopover}
        onClose={() => {
          setTablePopover(null)
        }}
      >
        {menuList && (
          <MenuList>
            {menuList.map((item, index) => (
              <MenuItem
                onClick={() => {
                  item.action()
                  setTablePopover(null)
                }}
                key={`menu-key-${index}`}
              >
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Popover>
    </Card>
  )
}

export default TableComponent
